import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { useBrainStore } from '@/state/brainStore';
import { useUIStore } from '@/state/uiStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useSessionStore } from '@/state/sessionStore';
import {
  generateDepositLink,
  simulateDepositLinkViewed,
  simulateStripeWebhookPaid,
  recordManualPayment,
} from '@/domain/depositFlow';
import {
  requiresNoDepositApproval,
  requestNoDepositOverride,
  waiveDepositDirectly,
} from '@/domain/approvals';
import { canApproveNoDepositOverride } from '@/domain/permissions';
import { onBookingConfirmed } from '@/domain/bookingConfirmationFlow';
import { generateInstalments } from '@/domain/topupPlan';
import { createTopupPlan, MIN_INSTALMENT_AUD } from '@/domain/topupBuilderFlow';
import { tierDurationMin, clampEndIso, holdExpiryHours, hourToIso } from '@/domain/depositWindow';
import { recommendChannel } from '@/domain/aiExtract';
import { SESSION_DEPOSIT_AUD, sessionLengthForDuration } from '@/domain/pricing';
import { customerName, aud, simTimeShort, simTimeFull, formatBodyPart } from '@/utils/format';
import type {
  Booking,
  CustomerRecord,
  DepositChannel,
  SessionLength,
  TopupFrequency,
} from '@/domain/types';
import ArtistDaySchedule from '@/components/queue/ArtistDaySchedule';
import { X, Send, Sparkles, MessageSquare, AtSign, Mail, Minus, Plus, Lock, Banknote, AlertTriangle } from 'lucide-react';
import clsx from 'clsx';

type LinkChannel = 'sms' | 'meta-dm' | 'ig-dm' | 'email';

// Diagram 02's four deposit methods, presented where the pay method is chosen.
// "Link" is the default Stripe send; the other three are the take-now methods,
// mapped onto the existing Deposit.method + DepositChannel unions (no type
// changes). Card over the phone and card in shop settle identically (eftpos /
// card); the labels keep the diagram's four names distinct at the surface.
type TakeNowMethodKey = 'card-phone' | 'card-shop' | 'cash';
const TAKE_NOW_METHODS: Array<{
  key: TakeNowMethodKey;
  label: string;
  method: 'eftpos' | 'cash';
  channel: DepositChannel;
}> = [
  { key: 'card-phone', label: 'Card over phone (EFTPOS)', method: 'eftpos', channel: 'card' },
  { key: 'card-shop', label: 'Card in shop', method: 'eftpos', channel: 'card' },
  { key: 'cash', label: 'Cash in shop', method: 'cash', channel: 'cash' },
];

// Amounts come from pricing.SESSION_DEPOSIT_AUD (single source of truth) - only
// the display copy lives here.
const SESSION_OPTIONS: Array<{
  value: SessionLength;
  label: string;
  description: string;
}> = [
  { value: 'small', label: 'Small', description: '90 min · single sitting' },
  { value: 'half-day', label: 'Half day', description: '4 hours · half-sleeve' },
  { value: 'full-day', label: 'Full day', description: '8 hours · sleeve / large piece' },
];

const CHANNEL_OPTIONS: Array<{
  value: LinkChannel;
  label: string;
  Icon: typeof MessageSquare;
}> = [
  { value: 'sms', label: 'SMS', Icon: MessageSquare },
  { value: 'meta-dm', label: 'Messenger', Icon: MessageSquare },
  { value: 'ig-dm', label: 'Instagram DM', Icon: AtSign },
  { value: 'email', label: 'Email', Icon: Mail },
];

interface Props {
  open: boolean;
  customer: CustomerRecord;
  onClose: () => void;
  /**
   * Slot discussed + pinned on the call. When present, the window opens in
   * PRE-FILLED mode: a wide two-column layout with booking details + an
   * adjustable session window on the left and the deposit + send on the right.
   * The tier is preselected from the slot length; the end + length are
   * adjustable (start stays locked). Absent opens FROM-SCRATCH: pick an artist,
   * a date and a free window before a deposit can be sent.
   */
  discussedSlot?: { artistId: string; startIso: string; endIso: string };
  /** Seeds the piece-name input (falls back to the design-intent notes). */
  initialPieceName?: string;
}

function recommendSession(customer: CustomerRecord): SessionLength {
  if (customer.designIntent?.size) return customer.designIntent.size;
  const hours = customer.designIntent?.estimatedHours ?? 0;
  if (hours >= 6) return 'full-day';
  if (hours >= 3) return 'half-day';
  return 'small';
}

function castChannel(c: string): LinkChannel {
  if (c === 'sms' || c === 'meta-dm' || c === 'ig-dm' || c === 'email') return c;
  return 'sms';
}

// Tier size order, for comparing the selected tier against the slot's natural
// bracket. A tier one bracket LARGER than the slot can hold is the overshoot.
const TIER_INDEX: SessionLength[] = ['small', 'half-day', 'full-day', 'multi-session'];

/** Duration of a slot in minutes. */
function slotMinutes(startIso: string, endIso: string): number {
  return Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60000);
}

/** Compact "3h" / "3h 15m" duration label. */
function durationLabel(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

// Hosts (My Queue, the slot pane, the composer) keep this modal permanently
// mounted and only toggle `open`. Seeding the slot/tier state once at first
// mount would therefore capture a still-empty slot and go stale the instant one
// is pinned after mount - which showed up as a blank Date and a missing session
// window in the grounded view. Remounting the body whenever the discussed slot
// changes re-seeds that state from the current props.
export default function DepositLinkModal(props: Props) {
  const slot = props.discussedSlot;
  return (
    <DepositLinkModalInner
      key={slot ? `${slot.artistId}-${slot.startIso}-${slot.endIso}` : 'scratch'}
      {...props}
    />
  );
}

function DepositLinkModalInner({
  open,
  customer,
  onClose,
  discussedSlot,
  initialPieceName,
}: Props) {
  const addConversationEntry = useBrainStore((s) => s.addConversationEntry);
  const upsertBooking = useBrainStore((s) => s.upsertBooking);
  const upsertCustomer = useBrainStore((s) => s.upsertCustomer);
  const appendActivity = useBrainStore((s) => s.appendActivity);
  const pushNotification = useUIStore((s) => s.pushNotification);
  const clockIso = useSimulationStore((s) => s.clockIso);
  const artistsById = useBrainStore((s) => s.artists);
  const piecesById = useBrainStore((s) => s.pieces);
  const persona = useSessionStore((s) => s.persona);

  const studioArtists = useMemo(
    () =>
      Object.values(artistsById)
        .filter((a) => a.studioId === customer.primaryStudioId && !a.archived)
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [artistsById, customer.primaryStudioId],
  );

  // Pre-filled mode preselects the tier from the picked slot's length; from
  // scratch falls back to the customer's design intent.
  const initialSession = discussedSlot
    ? sessionLengthForDuration(slotMinutes(discussedSlot.startIso, discussedSlot.endIso))
    : recommendSession(customer);
  const channelRec = useMemo(() => recommendChannel(customer), [customer]);
  const recommendedChannel = castChannel(channelRec.channel);

  const [session, setSession] = useState<SessionLength>(initialSession);
  const [channel, setChannel] = useState<LinkChannel>(recommendedChannel);
  const [pieceName, setPieceName] = useState(
    initialPieceName ?? customer.designIntent?.notes ?? '',
  );

  // Slot state. In pre-filled mode the block is seeded from the discussed slot;
  // from-scratch mode fills it in when the agent picks a free window.
  const [artistId, setArtistId] = useState(discussedSlot?.artistId ?? studioArtists[0]?.id ?? '');
  const [blockStartIso, setBlockStartIso] = useState<string | null>(
    discussedSlot?.startIso ?? null,
  );
  const [blockEndIso, setBlockEndIso] = useState<string | null>(discussedSlot?.endIso ?? null);
  const [endIso, setEndIso] = useState<string | null>(null);
  const [dateValue, setDateValue] = useState(() => format(new Date(clockIso), 'yyyy-MM-dd'));

  // Rate + deposit overrides. Empty string = follow the auto/standard value.
  const [dayRateInput, setDayRateInput] = useState('');
  const [depositInput, setDepositInput] = useState('');
  // Mandatory reason when the deposit is $0 (the no-deposit path). Approvals
  // rejects a blank note, so this gates the request/waive action.
  const [overrideReason, setOverrideReason] = useState('');
  // Phone collected + confirmed at payment (Diagram 02). Pre-filled from the
  // profile; a real number is required before a payment can complete, and the
  // confirmed number is stamped "on file". Name comes from the profile.
  const [phoneInput, setPhoneInput] = useState(customer.phone ?? '');
  // Which of the take-now methods (card over phone / card in shop / cash) the
  // agent is collecting through; the artist link is the separate Send action.
  const [takeNowMethod, setTakeNowMethod] = useState<TakeNowMethodKey>('card-phone');

  // Pay-over-time plan builder (tv-1a9). Off by default; when on, the deposit is
  // collected as instalments and the first one holds the date. The per-instalment
  // amount seeds from a sensible quarter-split of the tier's standard deposit,
  // clamped to the $50 floor.
  const [planEnabled, setPlanEnabled] = useState(false);
  const [planStartIso, setPlanStartIso] = useState(() => format(new Date(clockIso), 'yyyy-MM-dd'));
  const [planFrequency, setPlanFrequency] = useState<TopupFrequency>('fortnightly');
  const [planInstalmentAud, setPlanInstalmentAud] = useState(() =>
    Math.max(MIN_INSTALMENT_AUD, Math.round(SESSION_DEPOSIT_AUD[initialSession] / 4)),
  );

  const grounded = !!discussedSlot;
  const spec = SESSION_OPTIONS.find((s) => s.value === session) ?? SESSION_OPTIONS[1]!;
  const channelMeta = CHANNEL_OPTIONS.find((c) => c.value === channel) ?? CHANNEL_OPTIONS[0]!;

  const tierMin = tierDurationMin(session);
  const startIso = blockStartIso;
  const defaultEndIso = startIso
    ? new Date(new Date(startIso).getTime() + tierMin * 60000).toISOString()
    : null;
  const effectiveEndIso =
    startIso && blockEndIso ? clampEndIso(startIso, endIso ?? defaultEndIso!, blockEndIso) : null;
  // The window always clamps to the free block, so a shorter block is not an
  // error - the hold just runs to the block end. Overshoot warns only when the
  // agent picks a tier a full bracket LARGER than the slot can naturally hold
  // (e.g. a full-day deposit on a half-day window).
  const blockMin =
    startIso && blockEndIso ? slotMinutes(startIso, blockEndIso) : tierMin;
  const isOvershoot =
    !!(startIso && blockEndIso) &&
    TIER_INDEX.indexOf(session) > TIER_INDEX.indexOf(sessionLengthForDuration(blockMin));
  const hasSlot = !!(startIso && effectiveEndIso);
  const expiryHours = startIso ? holdExpiryHours(startIso, clockIso) : 48;

  // Held length in minutes (the ghost booking's duration). Falls back to the
  // tier length before a slot is grounded.
  const lengthMin =
    startIso && effectiveEndIso ? slotMinutes(startIso, effectiveEndIso) : tierMin;

  // Day the from-scratch schedule + picked window resolve against (local day).
  const dayIso = useMemo(() => new Date(`${dateValue}T00:00:00`).toISOString(), [dateValue]);

  const selectedArtist = artistsById[artistId];
  const selectedArtistName = selectedArtist?.displayName ?? artistId;
  const hourlyAud = selectedArtist?.rates.hourlyAud ?? 0;

  // Day rate auto-calculates from the artist's hourly rate x the session window;
  // the agent can override by typing. Deposit follows the tier standard until
  // overridden. The two are independent - changing one never forces the other.
  const autoDayRate = Math.round((lengthMin / 60) * hourlyAud);
  const effectiveDayRate = dayRateInput === '' ? autoDayRate : Math.max(0, Math.round(Number(dayRateInput) || 0));
  const standardDeposit = SESSION_DEPOSIT_AUD[session];
  const effectiveDeposit =
    depositInput === '' ? standardDeposit : Math.max(0, Math.round(Number(depositInput) || 0));

  // Plan schedule, recomputed each render so the preview tracks the inputs. The
  // date input is a local calendar day; anchor it at local midnight so the
  // rendered schedule shows the day the agent picked, not a UTC shift of it.
  const planStartIsoFull = new Date(`${planStartIso}T00:00:00`).toISOString();
  const planInvalid = planInstalmentAud < MIN_INSTALMENT_AUD;
  const planSchedule =
    effectiveDeposit > 0 && !planInvalid
      ? generateInstalments({
          startIso: planStartIsoFull,
          frequency: planFrequency,
          instalmentAud: planInstalmentAud,
          totalAud: effectiveDeposit,
        })
      : [];

  const styleLabel = customer.preferredStyle?.replace('-', ' ');
  const placement = customer.designIntent?.placement;
  // Prefer the brief's explicit colour; fall back to deriving from the design type.
  const colour: 'colour' | 'black-and-grey' | undefined =
    customer.designIntent?.colour ??
    (customer.designIntent
      ? /colou?r/.test(customer.designIntent.type)
        ? 'colour'
        : 'black-and-grey'
      : undefined);
  const collabArtistIds = customer.designIntent?.collabArtistIds ?? [];
  const briefPieceId =
    customer.designIntent?.pieceMode === 'continue' ? customer.designIntent?.continuePieceId : undefined;
  const briefRefs = customer.designIntent?.referencePhotos ?? [];
  const bodyPart = customer.designIntent?.bodyPart;
  const bodyPartSide = customer.designIntent?.bodyPartSide;
  const bodyPartQualifier = customer.designIntent?.bodyPartQualifier;

  // Read-only brief summaries for the booking-details card - these mirror the
  // segmented brief in the left rail (piece / style & artist / placement / look
  // / references) so the deposit confirms the same thing the agent just built.
  const di = customer.designIntent;
  const pieceMode = di?.pieceMode;
  const continuedPiece = di?.continuePieceId ? piecesById[di.continuePieceId] : undefined;
  const pieceSummary =
    pieceMode === 'continue'
      ? continuedPiece
        ? `${continuedPiece.title || continuedPiece.style || continuedPiece.designType.replace(/-/g, ' ')} · continuing`
        : 'Continuing a piece'
      : di?.newPieceTitle
        ? pieceMode === 'one-off'
          ? `${di.newPieceTitle} · one-off`
          : `${di.newPieceTitle}${di.estimatedSessions ? ` · ${di.estimatedSessions} sessions` : ''}`
        : undefined;
  // One formatter for the whole app (was a second, differently-ordered one here).
  // Falls back to the free-text placement, which is what the picker's "Other"
  // pill writes, so an unlisted body part still reads correctly on the brief.
  const placementLabel = formatBodyPart({ bodyPart, bodyPartSide, bodyPartQualifier, placement });
  const sizeLabel =
    di?.size === 'small'
      ? 'Small'
      : di?.size === 'half-day'
        ? 'Half day'
        : di?.size === 'full-day'
          ? 'Full day'
          : di?.size === 'multi-session'
            ? 'Multi-session'
            : undefined;
  const collabNames = collabArtistIds.map((id) => artistsById[id]?.displayName ?? id);

  // Picking a tier is the primary control: it snaps the window back to the
  // tier's default length and resets the rate/deposit overrides, so the auto
  // values track the tier until the agent adjusts them again.
  const changeSession = (s: SessionLength) => {
    setSession(s);
    setEndIso(null);
    setDayRateInput('');
    setDepositInput('');
  };

  const stepEnd = (deltaMin: number) => {
    if (!startIso || !blockEndIso) return;
    const base = endIso ?? defaultEndIso!;
    setEndIso(
      clampEndIso(
        startIso,
        new Date(new Date(base).getTime() + deltaMin * 60000).toISOString(),
        blockEndIso,
      ),
    );
  };

  const onPickWindow = (w: { startHour: number; endHour: number }) => {
    setBlockStartIso(hourToIso(dayIso, w.startHour));
    setBlockEndIso(hourToIso(dayIso, w.endHour));
    setEndIso(null);
  };

  // End stepper bounds, for accessible disabled states. Shared by the end and
  // length steppers (length just steps the end).
  const canDecEnd = !!(
    effectiveEndIso &&
    startIso &&
    new Date(effectiveEndIso).getTime() > new Date(startIso).getTime() + 15 * 60000
  );
  const canIncEnd = !!(
    effectiveEndIso &&
    blockEndIso &&
    new Date(effectiveEndIso).getTime() < new Date(blockEndIso).getTime()
  );

  // The slot is ready to hang a booking off once a window is picked and the tier
  // does not overshoot it. Shared by every send/take/no-deposit action.
  const slotReady = hasSlot && !isOvershoot;
  // A $0 deposit is the no-deposit path: privileged personas waive it directly
  // (note-forced), everyone else raises an approval request. Both replace the
  // Stripe-link send. Mirrors NewBookingModal's controlled no-deposit routing.
  const needsApproval = requiresNoDepositApproval(effectiveDeposit);
  const isPrivileged = persona ? canApproveNoDepositOverride(persona) : false;
  const canSubmitNoDeposit = slotReady && overrideReason.trim().length > 0;
  // A real phone is mandatory before a payment can complete (Diagram 02). It is
  // pre-filled from the profile, so this is a confirm for agent-sourced clients;
  // it still blocks a blanked-out number.
  const trimmedPhone = phoneInput.trim();
  const canTakeNow = slotReady && effectiveDeposit > 0 && trimmedPhone.length > 0;
  const takeNowSpec =
    TAKE_NOW_METHODS.find((m) => m.key === takeNowMethod) ?? TAKE_NOW_METHODS[0]!;

  // Held/confirmed ghost booking built from the picked slot + brief, so the
  // no-deposit request and take-now payment have a real booking to attach to.
  // Mirrors the held booking generateDepositLink writes, minus the deposit.
  const buildBooking = (id: string, status: Booking['status']): Booking => {
    const trimmedPieceName = pieceName.trim();
    const styleTags = customer.preferredStyle ? [customer.preferredStyle] : [];
    return {
      id,
      customerId: customer.id,
      studioId: customer.primaryStudioId,
      artistId,
      start: startIso!,
      durationMin: lengthMin,
      sessionLength: session,
      status,
      designBrief: trimmedPieceName || customer.designIntent?.notes || 'Booking from deposit screen.',
      referencePhotos: briefRefs,
      source: 'agent-initiated-chat',
      createdAt: clockIso,
      dayRateAud: effectiveDayRate,
      ...(placement ? { placement } : {}),
      ...(bodyPart ? { bodyPart } : {}),
      ...(bodyPartSide ? { bodyPartSide } : {}),
      ...(bodyPartQualifier ? { bodyPartQualifier } : {}),
      ...(styleTags.length ? { styleTags } : {}),
      ...(colour ? { colour } : {}),
      ...(collabArtistIds.length ? { collabArtistIds } : {}),
      ...(briefPieceId ? { pieceId: briefPieceId } : {}),
    };
  };

  // Create the held booking both no-deposit paths hang off (reuses the picked
  // slot, grounded or from-scratch). Returns the booking id, or undefined if the
  // slot is not ready.
  const createHeldBooking = (): string | undefined => {
    if (!slotReady || !startIso) return undefined;
    const id = `bk-${Date.now()}`;
    const booking = buildBooking(id, 'held');
    upsertBooking(booking);
    appendActivity({
      id: `act-bk-${id}`,
      type: 'booking-created',
      customerId: customer.id,
      studioId: booking.studioId,
      artistId,
      body: `Held ${simTimeFull(startIso)} for ${customerName(customer)} with ${selectedArtistName} - no deposit.`,
      timestamp: clockIso,
    });
    return id;
  };

  // $0 path: raise a no-deposit approval (non-privileged) or waive directly
  // (Georgina/Owner). Note is mandatory - approvals refuses a blank one.
  const submitNoDeposit = () => {
    if (!canSubmitNoDeposit) return;
    const id = createHeldBooking();
    if (!id) return;
    const note = overrideReason.trim();
    if (isPrivileged) {
      waiveDepositDirectly({ bookingId: id, by: persona ?? 'front-desk', note });
      pushNotification({
        intensity: 'high',
        title: 'Booking confirmed - no deposit',
        body: 'Deposit waived and logged. Customer pays in full on the day.',
        ttlMs: 4500,
      });
    } else {
      requestNoDepositOverride({ bookingId: id, requestedBy: persona ?? 'front-desk', note });
      pushNotification({
        intensity: 'high',
        title: 'Sent for approval',
        body: 'Slot held. Georgina will approve or reject the no-deposit request.',
        ttlMs: 5000,
      });
    }
    onClose();
  };

  // Take payment now (the selected in-person / over-the-phone method): confirm
  // the booking on the spot and record the deposit as a manual payment. Mirrors
  // NewBookingModal.takeManual - no reminder cadence. The phone is confirmed +
  // stamped on file first (recordManualPayment's receipt reads it back).
  const takePaymentNow = () => {
    if (!canTakeNow || !startIso) return;
    // Persist the confirmed / edited number so an edit sticks; the receipt path
    // then stamps numberOnFile against the fresh record.
    if (trimmedPhone !== customer.phone) {
      upsertCustomer({ ...customer, phone: trimmedPhone });
    }
    const id = `bk-${Date.now()}`;
    const booking = buildBooking(id, 'confirmed');
    upsertBooking(booking);
    appendActivity({
      id: `act-bk-${id}`,
      type: 'booking-created',
      customerId: customer.id,
      studioId: booking.studioId,
      artistId,
      body: `Confirmed ${simTimeFull(startIso)} for ${customerName(customer)} with ${selectedArtistName}.`,
      timestamp: clockIso,
    });
    recordManualPayment({
      bookingId: id,
      amountAud: effectiveDeposit,
      method: takeNowSpec.method,
      channel: takeNowSpec.channel,
      by: persona ?? 'front-desk',
    });
    // Booking is confirmed on the spot - auto-send its waiver + invite.
    onBookingConfirmed(id);
    pushNotification({
      intensity: 'high',
      title: 'Deposit paid - receipt sent',
      body: `${aud(effectiveDeposit)} by ${takeNowSpec.label.toLowerCase()} - booking confirmed. Receipt sent, number on file.`,
      ttlMs: 4500,
      customerId: customer.id,
    });
    onClose();
  };

  const onSend = () => {
    if (!hasSlot || isOvershoot || !startIso || !effectiveEndIso) return;
    // A $0 deposit never sends a link - it routes through the no-deposit path.
    if (needsApproval) return;
    const trimmedPieceName = pieceName.trim();
    const styleTags = customer.preferredStyle ? [customer.preferredStyle] : [];
    const dep = generateDepositLink({
      customerId: customer.id,
      studioId: customer.primaryStudioId,
      artistId,
      amountAud: effectiveDeposit,
      channel,
      sessionLength: session,
      source: 'agent-initiated-chat',
      discussedSlot: { artistId, startIso, endIso: effectiveEndIso },
      dayRateAud: effectiveDayRate,
      ...(trimmedPieceName ? { pieceName: trimmedPieceName } : {}),
      ...(placement ? { placement } : {}),
      ...(bodyPart ? { bodyPart } : {}),
      ...(bodyPartSide ? { bodyPartSide } : {}),
      ...(bodyPartQualifier ? { bodyPartQualifier } : {}),
      ...(styleTags.length ? { styleTags } : {}),
      ...(colour ? { colour } : {}),
      ...(collabArtistIds.length ? { collabArtistIds } : {}),
      ...(briefPieceId ? { pieceId: briefPieceId } : {}),
      ...(briefRefs.length ? { referencePhotos: briefRefs } : {}),
    });
    addConversationEntry(customer.id, {
      id: `convo-dep-${dep.id}`,
      customerId: customer.id,
      channel,
      direction: 'out',
      body: `Sent ${customerName(customer)} a ${aud(effectiveDeposit)} deposit link for a ${spec.label.toLowerCase()} session.`,
      timestamp: clockIso,
      authorRole: 'agent',
      authorName: 'You',
      depositId: dep.id,
    });
    pushNotification({
      intensity: 'info',
      title: planEnabled ? 'Deposit plan sent' : 'Deposit link sent',
      body: `${aud(effectiveDeposit)} on ${channelMeta.label}. Watch the conversation for the lifecycle.`,
      ttlMs: 4000,
    });
    // Plan branch: build the pay-over-time plan against the fresh deposit and let
    // payment arrive through the instalments / tap-to-pay page - NOT the single
    // full-amount webhook. Toggle OFF keeps the original single-payment lifecycle.
    if (planEnabled && !planInvalid) {
      createTopupPlan({
        depositId: dep.id,
        startIso: planStartIsoFull,
        frequency: planFrequency,
        instalmentAud: planInstalmentAud,
        at: clockIso,
        by: persona ?? undefined,
      });
    }
    // Simulated lifecycle: viewed at ~1.8s, paid at ~3.6s.
    setTimeout(() => simulateDepositLinkViewed(dep.id), 1800);
    if (!planEnabled) {
      setTimeout(() => simulateStripeWebhookPaid({ depositId: dep.id }), 3600);
    }
    onClose();
  };

  const sendDisabled = !hasSlot || isOvershoot || (planEnabled && planInvalid);

  // ---- Shared building blocks (composed differently per mode) ---------------

  const pieceNameInput = (
    <div>
      <label htmlFor="deposit-piece-name" className="label-small-caps mb-1.5 block">
        Piece name - optional
      </label>
      <input
        id="deposit-piece-name"
        type="text"
        value={pieceName}
        onChange={(e) => setPieceName(e.target.value)}
        data-testid="deposit-piece-name"
        placeholder="e.g. Jesus back piece"
        className="w-full rounded-md border border-gold-dim/30 bg-ink-deep px-3 py-2 font-serif text-sm text-cream placeholder:text-cream-faint focus:border-gold-bright focus:outline-none"
      />
    </div>
  );

  const tiersBlock = (
    <section>
      <p className="label-small-caps mb-2">Session length</p>
      <div className="grid grid-cols-3 gap-2">
        {SESSION_OPTIONS.map((opt) => {
          const active = session === opt.value;
          const recommended = opt.value === initialSession;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => changeSession(opt.value)}
              data-testid={`deposit-session-${opt.value}`}
              className={clsx(
                'relative rounded-lg border px-3 py-3 text-left transition',
                active
                  ? 'border-gold-bright bg-gold/10'
                  : 'border-gold-dim/30 hover:border-gold-bright/60',
              )}
            >
              {recommended ? (
                <span className="absolute top-1.5 right-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm text-[8px] font-inter uppercase tracking-widest bg-mint/30 text-mint">
                  <Sparkles size={8} /> Rec
                </span>
              ) : null}
              <p className="font-inter text-base text-cream">{opt.label}</p>
              <p className="font-mono text-[9px] uppercase tracking-widest text-cream-faint mt-0.5">
                {durationLabel(tierDurationMin(opt.value))}
              </p>
              <p className="font-cinzel text-lg text-gold-bright mt-0.5">
                {aud(SESSION_DEPOSIT_AUD[opt.value])}
              </p>
            </button>
          );
        })}
      </div>
    </section>
  );

  const customBlock = (
    <section data-testid="deposit-custom-rate">
      <p className="label-small-caps mb-2">Custom rate / deposit</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="deposit-day-rate" className="font-inter text-[10px] uppercase tracking-widest text-cream-faint block mb-1">
            Day rate (AUD)
          </label>
          <input
            id="deposit-day-rate"
            type="number"
            min={0}
            value={dayRateInput}
            onChange={(e) => setDayRateInput(e.target.value)}
            placeholder={String(autoDayRate)}
            data-testid="deposit-day-rate"
            className="w-full rounded-md border border-gold-dim/30 bg-ink-deep px-3 py-2 font-serif text-sm text-cream placeholder:text-cream-faint focus:border-gold-bright focus:outline-none"
          />
          <p className="font-mono text-[10px] uppercase tracking-widest text-cream-faint mt-1">
            {durationLabel(lengthMin)} × {aud(hourlyAud)}/h = {aud(autoDayRate)} · editable
          </p>
        </div>
        <div>
          <label htmlFor="deposit-amount" className="font-inter text-[10px] uppercase tracking-widest text-cream-faint block mb-1">
            Deposit (AUD)
          </label>
          <input
            id="deposit-amount"
            type="number"
            min={0}
            value={depositInput}
            onChange={(e) => setDepositInput(e.target.value)}
            placeholder={String(standardDeposit)}
            data-testid="deposit-amount"
            className="w-full rounded-md border border-gold-dim/30 bg-ink-deep px-3 py-2 font-serif text-sm text-cream placeholder:text-cream-faint focus:border-gold-bright focus:outline-none"
          />
          <p className="font-mono text-[10px] uppercase tracking-widest text-cream-faint mt-1">
            Standard {aud(standardDeposit)} · $0 = no deposit
          </p>
        </div>
      </div>
    </section>
  );

  // Pay-over-time plan builder, revealed by the toggle. Sits directly under the
  // custom rate / deposit section in both layouts.
  const topupBlock = (
    <section data-testid="deposit-topup" className="rounded-lg border border-gold-dim/30 p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="label-small-caps">Pay the deposit over time</p>
          <p className="font-serif text-[11px] text-cream-soft mt-0.5">
            Split the {aud(effectiveDeposit)} deposit into instalments. The first holds the date.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={planEnabled}
          aria-label="Pay the deposit over time"
          onClick={() => setPlanEnabled((v) => !v)}
          data-testid="deposit-topup-toggle"
          className={clsx(
            'relative h-6 w-11 shrink-0 rounded-full border transition',
            planEnabled ? 'bg-gold/30 border-gold-bright' : 'bg-ink-deep border-gold-dim/40',
          )}
        >
          <span
            className={clsx(
              'absolute top-0.5 h-4 w-4 rounded-full transition-all',
              planEnabled ? 'left-6 bg-gold-bright' : 'left-0.5 bg-cream-faint',
            )}
          />
        </button>
      </div>

      {planEnabled ? (
        <div className="mt-3 space-y-3" data-testid="deposit-topup-builder">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="deposit-topup-start"
                className="font-inter text-[10px] uppercase tracking-widest text-cream-faint block mb-1"
              >
                First instalment
              </label>
              <input
                id="deposit-topup-start"
                type="date"
                value={planStartIso}
                onChange={(e) => setPlanStartIso(e.target.value)}
                data-testid="deposit-topup-start"
                className="w-full rounded-md border border-gold-dim/30 bg-ink-deep px-3 py-2 font-serif text-sm text-cream focus:border-gold-bright focus:outline-none"
              />
            </div>
            <div>
              <label
                htmlFor="deposit-topup-amount"
                className="font-inter text-[10px] uppercase tracking-widest text-cream-faint block mb-1"
              >
                Per instalment (AUD)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 font-serif text-sm text-cream-faint">
                  $
                </span>
                <input
                  id="deposit-topup-amount"
                  type="number"
                  min={MIN_INSTALMENT_AUD}
                  value={planInstalmentAud}
                  onChange={(e) =>
                    setPlanInstalmentAud(Math.max(0, Math.round(Number(e.target.value) || 0)))
                  }
                  data-testid="deposit-topup-amount"
                  className="w-full rounded-md border border-gold-dim/30 bg-ink-deep pl-6 pr-3 py-2 font-serif text-sm text-cream focus:border-gold-bright focus:outline-none"
                />
              </div>
            </div>
          </div>

          <div>
            <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint mb-1">
              Cadence
            </p>
            <div className="inline-flex overflow-hidden rounded-md border border-gold-dim/30">
              {(['weekly', 'fortnightly'] as TopupFrequency[]).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setPlanFrequency(f)}
                  data-testid={`deposit-topup-freq-${f}`}
                  className={clsx(
                    'px-3 py-1.5 font-inter text-xs capitalize transition',
                    planFrequency === f
                      ? 'bg-gold/10 text-gold-bright'
                      : 'text-cream-soft hover:text-cream',
                  )}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          {planInvalid ? (
            <p className="font-serif text-[12px] text-blood" data-testid="deposit-topup-invalid">
              Each instalment must be at least {aud(MIN_INSTALMENT_AUD)}.
            </p>
          ) : (
            <div data-testid="deposit-topup-schedule">
              <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint mb-1.5">
                Schedule · {planSchedule.length} instalments
              </p>
              <ul className="space-y-1">
                {planSchedule.map((inst, i) => (
                  <li
                    key={inst.dueIso}
                    data-testid={`deposit-topup-instalment-${i}`}
                    className="flex items-center justify-between font-serif text-[13px] text-cream"
                  >
                    <span>
                      {format(new Date(inst.dueIso), 'EEE d MMM')}
                      {i === 0 ? (
                        <span className="ml-2 font-inter text-[9px] uppercase tracking-widest text-gold-bright">
                          holds the date
                        </span>
                      ) : null}
                    </span>
                    <span className="tabular-nums">{aud(inst.amountAud)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : null}
    </section>
  );

  const channelBlock = (
    <section>
      <p className="label-small-caps mb-2">Send via</p>
      <div className="flex flex-wrap gap-2">
        {CHANNEL_OPTIONS.map((opt) => {
          const active = channel === opt.value;
          const recommended = opt.value === recommendedChannel;
          const Icon = opt.Icon;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => setChannel(opt.value)}
              data-testid={`deposit-channel-${opt.value}`}
              className={clsx(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs transition',
                active
                  ? 'border-gold-bright bg-gold/10 text-gold-bright'
                  : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/60',
              )}
            >
              <Icon size={12} /> {opt.label}
              {recommended ? (
                <span className="ml-1 px-1 py-0.5 rounded-sm text-[8px] font-inter uppercase tracking-widest bg-mint/30 text-mint">
                  Rec
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </section>
  );

  const previewBlock = (
    <section className="panel-dark p-3">
      <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
        Preview ({channelMeta.label})
      </p>
      {planEnabled && !planInvalid ? (
        <p className="font-serif text-sm text-cream mt-2 leading-relaxed" data-testid="deposit-preview-plan">
          Hey {customer.firstName}, here&apos;s your deposit plan for the {spec.label.toLowerCase()}{' '}
          session: {planSchedule.length} instalments of about {aud(planInstalmentAud)}, {planFrequency}{' '}
          from {format(new Date(planStartIsoFull), 'EEE d MMM')}. The first payment holds your date.
        </p>
      ) : (
        <p className="font-serif text-sm text-cream mt-2 leading-relaxed">
          Hey {customer.firstName}, here&apos;s your deposit link for the {spec.label.toLowerCase()}{' '}
          session: {aud(effectiveDeposit)}. The link expires in {expiryHours}h. Once you&apos;ve paid
          we&apos;ll lock in a time that works for you.
        </p>
      )}
    </section>
  );

  // Booking-details confirmation card (pre-filled mode). Read-only summary of
  // the brief + slot, with the piece name still editable.
  const bookingDetailsBlock = (
    <section
      className="rounded-lg border border-gold-dim/30 p-3 space-y-3"
      data-testid="deposit-discussed-slot"
    >
      <p className="font-inter text-[10px] uppercase tracking-widest text-gold-bright">
        Booking details · from the brief - not shown to the customer
      </p>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
        <div className="col-span-2">
          <dt className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">Piece</dt>
          <dd className="font-serif text-sm text-cream">{pieceSummary ?? '—'}</dd>
        </div>
        <div>
          <dt className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">Artist</dt>
          <dd className="font-serif text-sm text-cream inline-flex items-center gap-1">
            <Lock size={10} className="text-cream-faint" /> {selectedArtistName}
          </dd>
        </div>
        <div>
          <dt className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">Date</dt>
          <dd className="font-serif text-sm text-cream">
            {startIso ? format(new Date(startIso), 'EEE d MMM') : ''}
          </dd>
        </div>
        {collabNames.length ? (
          <div className="col-span-2">
            <dt className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">
              Collab
            </dt>
            <dd className="font-serif text-sm text-cream">{collabNames.join(', ')}</dd>
          </div>
        ) : null}
        <div>
          <dt className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">Style</dt>
          <dd className="font-serif text-sm text-cream capitalize">{styleLabel ?? '—'}</dd>
        </div>
        <div>
          <dt className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">Size</dt>
          <dd className="font-serif text-sm text-cream">{sizeLabel ?? '—'}</dd>
        </div>
        <div>
          <dt className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">
            Placement
          </dt>
          <dd className="font-serif text-sm text-cream">
            <span className="capitalize">{placementLabel || '—'}</span>
            {/* Legacy records can carry both a structured part and a free-text
                note; keep the note visible rather than letting the formatter
                swallow it. */}
            {bodyPart && placement ? (
              <span className="block text-[12px] text-cream-soft">{placement}</span>
            ) : null}
          </dd>
        </div>
        <div>
          <dt className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">Colour</dt>
          <dd className="font-serif text-sm text-cream">
            {colour === 'colour' ? 'Colour' : colour === 'black-and-grey' ? 'Black & grey' : '—'}
          </dd>
        </div>
        {di?.notes ? (
          <div className="col-span-2">
            <dt className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">
              Notes
            </dt>
            <dd className="font-serif text-sm text-cream">{di.notes}</dd>
          </div>
        ) : null}
        {di?.notes ? (
          <div className="col-span-2">
            <dt className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">Notes</dt>
            <dd className="font-serif text-[13px] text-cream-soft">{di.notes}</dd>
          </div>
        ) : null}
      </dl>
      {briefRefs.length ? (
        <div data-testid="deposit-brief-refs">
          <p className="font-inter text-[9px] uppercase tracking-widest text-cream-faint mb-1.5">
            References · {briefRefs.length}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {briefRefs.slice(0, 6).map((url) => (
              <div
                key={url}
                className="h-10 w-10 overflow-hidden rounded-md border border-gold-dim/30"
              >
                <img src={url} alt="" className="h-full w-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {pieceNameInput}
      <p className="font-serif text-[11px] text-cream-soft">
        Confirm before sending - edit on the brief. The full brief rides onto the held booking.
      </p>
    </section>
  );

  // Session window: start locked to the slot; end + length adjustable and kept
  // in sync (length just steps the end), clamped to the free block.
  const sessionWindowBlock =
    startIso && blockEndIso ? (
      <section
        className="rounded-lg border border-gold-dim/30 p-3"
        data-testid="deposit-session-window"
      >
        <p className="label-small-caps mb-3">Session window</p>
        <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
          <div>
            <p className="font-inter text-[9px] uppercase tracking-widest text-cream-faint mb-1 inline-flex items-center gap-1">
              <Lock size={9} /> Start
            </p>
            <p className="font-serif text-sm text-cream" data-testid="deposit-start">
              {simTimeShort(startIso)}
            </p>
          </div>
          <div>
            <p className="font-inter text-[9px] uppercase tracking-widest text-cream-faint mb-1">End</p>
            <div className="inline-flex items-center gap-2">
              <button
                type="button"
                onClick={() => stepEnd(-15)}
                disabled={!canDecEnd}
                aria-label="End 15 minutes earlier"
                data-testid="deposit-end-minus"
                className="btn-quiet disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Minus size={12} />
              </button>
              <span
                className="font-serif text-sm text-cream min-w-[4.5rem] text-center"
                data-testid="deposit-end"
              >
                {effectiveEndIso ? simTimeShort(effectiveEndIso) : ''}
              </span>
              <button
                type="button"
                onClick={() => stepEnd(15)}
                disabled={!canIncEnd}
                aria-label="End 15 minutes later"
                data-testid="deposit-end-plus"
                className="btn-quiet disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus size={12} />
              </button>
            </div>
          </div>
          <div>
            <p className="font-inter text-[9px] uppercase tracking-widest text-cream-faint mb-1">Length</p>
            <div className="inline-flex items-center gap-2">
              <button
                type="button"
                onClick={() => stepEnd(-15)}
                disabled={!canDecEnd}
                aria-label="15 minutes shorter"
                data-testid="deposit-length-minus"
                className="btn-quiet disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Minus size={12} />
              </button>
              <span
                className="font-serif text-sm text-cream min-w-[4.5rem] text-center"
                data-testid="deposit-length"
              >
                {durationLabel(lengthMin)}
              </span>
              <button
                type="button"
                onClick={() => stepEnd(15)}
                disabled={!canIncEnd}
                aria-label="15 minutes longer"
                data-testid="deposit-length-plus"
                className="btn-quiet disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus size={12} />
              </button>
            </div>
          </div>
        </div>
        {isOvershoot ? (
          <p className="mt-2 font-serif text-[12px] text-blood" data-testid="deposit-overshoot">
            A {spec.label.toLowerCase()} session ({tierMin} min) runs past this window. Shorten the
            tier or pick a longer slot.
          </p>
        ) : null}
        {hasSlot && !isOvershoot ? (
          <p className="mt-2 font-serif text-[11px] text-cream-soft" data-testid="deposit-expiry">
            Hold expires in {expiryHours}h once the link is sent.
          </p>
        ) : null}
      </section>
    ) : null;

  const fromScratchPicker = (
    <section data-testid="deposit-slot-picker">
      <p className="label-small-caps mb-2">Artist + date</p>
      <div className="flex flex-wrap gap-2">
        <select
          aria-label="Artist"
          value={artistId}
          onChange={(e) => {
            setArtistId(e.target.value);
            setBlockStartIso(null);
            setBlockEndIso(null);
            setEndIso(null);
          }}
          data-testid="deposit-artist-select"
          className="flex-1 min-w-[10rem] rounded-md border border-gold-dim/30 bg-ink-deep px-3 py-2 font-serif text-sm text-cream focus:border-gold-bright focus:outline-none"
        >
          {studioArtists.length === 0 ? (
            <option value="">No artists at this studio</option>
          ) : (
            studioArtists.map((a) => (
              <option key={a.id} value={a.id}>
                {a.displayName}
              </option>
            ))
          )}
        </select>
        <input
          type="date"
          aria-label="Date"
          value={dateValue}
          onChange={(e) => {
            setDateValue(e.target.value);
            setBlockStartIso(null);
            setBlockEndIso(null);
            setEndIso(null);
          }}
          data-testid="deposit-date"
          className="rounded-md border border-gold-dim/30 bg-ink-deep px-3 py-2 font-serif text-sm text-cream focus:border-gold-bright focus:outline-none"
        />
      </div>
      <div className="mt-3">
        <ArtistDaySchedule artistId={artistId} dayIso={dayIso} onPickWindow={onPickWindow} />
      </div>
      {!startIso ? (
        <p className="font-serif text-[11px] text-cream-soft" data-testid="deposit-pick-prompt">
          Pick a free window above to ground the deposit.
        </p>
      ) : null}
    </section>
  );

  // No-deposit block, shown only when the deposit is $0. A reason is mandatory
  // (approvals refuses a blank note); privileged personas waive directly, the
  // rest raise a request. Mirrors NewBookingModal's no-deposit surface.
  const noDepositBlock = needsApproval ? (
    <section
      className="rounded-md border border-amber-400/40 bg-amber-400/5 p-3"
      data-testid="deposit-no-deposit-block"
    >
      <p className="flex items-start gap-1.5 text-[12px] text-amber-300">
        <AlertTriangle size={13} className="mt-px shrink-0" />
        {isPrivileged
          ? 'No deposit on this booking. Waiving it needs a reason - it will be logged against the booking.'
          : "No deposit on this booking. It can't be sent directly - send it to Georgina for no-deposit approval."}
      </p>
      <label className="mt-2 block">
        <span className="label-small-caps">
          No-deposit reason <span className="text-blood">*</span>
        </span>
        <textarea
          value={overrideReason}
          onChange={(e) => setOverrideReason(e.target.value)}
          rows={2}
          placeholder="e.g. trusted repeat customer, paying full on the day"
          data-testid="deposit-no-deposit-reason"
          className="mt-2 w-full rounded-md border border-gold-dim/30 bg-ink-deep px-3 py-2 font-serif text-sm text-cream placeholder:text-cream-faint focus:border-gold-bright focus:outline-none"
        />
      </label>
    </section>
  ) : null;

  // Payment contact + method (Diagram 02), shown for a positive deposit. Presents
  // the four methods where the pay method is chosen (artist link = the Send
  // button; the three take-now pills), and collects the mandatory phone (+ name)
  // that the receipt is sent to and that goes on file for SMS.
  const paymentContactBlock = !needsApproval ? (
    <section
      className="rounded-lg border border-gold-dim/30 p-3 space-y-3"
      data-testid="deposit-payment-contact"
    >
      <div>
        <p className="label-small-caps mb-1.5">How are they paying?</p>
        <p className="font-serif text-[11px] text-cream-soft mb-2">
          The artist link is the Send button; the other three take payment now on the spot.
        </p>
        <div className="flex flex-wrap gap-2">
          <span
            className="inline-flex items-center gap-1 rounded-md border border-gold-dim/30 px-3 py-1.5 text-xs text-cream-soft"
            data-testid="deposit-paymethod-link"
          >
            <Send size={12} /> Artist link
          </span>
          {TAKE_NOW_METHODS.map((m) => {
            const active = takeNowMethod === m.key;
            return (
              <button
                key={m.key}
                type="button"
                onClick={() => setTakeNowMethod(m.key)}
                data-testid={`deposit-paymethod-${m.key}`}
                className={clsx(
                  'inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-xs transition',
                  active
                    ? 'border-gold-bright bg-gold/10 text-gold-bright'
                    : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/60',
                )}
              >
                <Banknote size={12} /> {m.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint mb-1">
            Name
          </p>
          <p className="font-serif text-sm text-cream" data-testid="deposit-contact-name">
            {customerName(customer)}
          </p>
        </div>
        <div>
          <label
            htmlFor="deposit-phone"
            className="font-inter text-[10px] uppercase tracking-widest text-cream-faint block mb-1"
          >
            Phone <span className="text-blood">*</span>
          </label>
          <input
            id="deposit-phone"
            type="tel"
            value={phoneInput}
            onChange={(e) => setPhoneInput(e.target.value)}
            data-testid="deposit-phone"
            placeholder="+61 4XX XXX XXX"
            className="w-full rounded-md border border-gold-dim/30 bg-ink-deep px-3 py-2 font-serif text-sm text-cream placeholder:text-cream-faint focus:border-gold-bright focus:outline-none"
          />
        </div>
      </div>
      <p className="font-serif text-[11px] text-cream-soft">
        A number is required to take payment: it receives the receipt and goes on file for SMS.
      </p>
    </section>
  ) : null;

  const sendFooter = (
    <footer className="flex flex-wrap justify-end gap-2 mt-5">
      <button
        type="button"
        onClick={onClose}
        className="btn-quiet text-xs"
        data-testid="deposit-modal-cancel"
      >
        Cancel
      </button>
      {needsApproval ? (
        isPrivileged ? (
          <button
            type="button"
            onClick={submitNoDeposit}
            disabled={!canSubmitNoDeposit}
            className="btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="deposit-waive-no-deposit"
            title={
              canSubmitNoDeposit ? 'Confirm with no deposit' : 'Add a reason to waive the deposit'
            }
          >
            Confirm - no deposit
          </button>
        ) : (
          <button
            type="button"
            onClick={submitNoDeposit}
            disabled={!canSubmitNoDeposit}
            className="btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="deposit-request-no-deposit"
            title={
              canSubmitNoDeposit
                ? 'Send for no-deposit approval'
                : 'Add a reason to request approval'
            }
          >
            Request no-deposit approval
          </button>
        )
      ) : (
        <>
          <button
            type="button"
            onClick={takePaymentNow}
            disabled={!canTakeNow}
            className="btn-quiet text-xs disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="deposit-take-payment-now"
            title={
              !slotReady
                ? 'Pick a slot that fits the tier first'
                : trimmedPhone.length === 0
                  ? 'Add a phone number to take payment'
                  : `Take ${aud(effectiveDeposit)} by ${takeNowSpec.label.toLowerCase()} now`
            }
          >
            <Banknote size={12} /> Take {aud(effectiveDeposit)} now
          </button>
          <button
            type="button"
            onClick={onSend}
            disabled={sendDisabled}
            className="btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="deposit-modal-send"
          >
            <Send size={12} />{' '}
            {planEnabled && !planInvalid
              ? `Send plan · ${planSchedule.length} × ${aud(planInstalmentAud)} · hold slot`
              : `Send ${aud(effectiveDeposit)} link · hold slot`}
          </button>
        </>
      )}
    </footer>
  );

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-overlay/70 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={onClose}
          data-testid="deposit-link-modal"
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            className={clsx(
              'panel bg-ink-deep px-6 py-5 w-full max-h-[90vh] overflow-y-auto shadow-gold-glow',
              grounded ? 'max-w-4xl' : 'max-w-xl',
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex items-start justify-between mb-4">
              <div>
                <p className="label-small-caps text-gold-bright">Send deposit link</p>
                <h2 className="heading-display text-2xl text-cream mt-1">
                  {customerName(customer)}
                </h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="btn-quiet"
                aria-label="Close"
                data-testid="deposit-modal-close"
              >
                <X size={14} />
              </button>
            </header>

            {grounded ? (
              // Pre-filled: wide two columns - confirm + when | deposit + send.
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-4">
                  {bookingDetailsBlock}
                  {sessionWindowBlock}
                </div>
                <div className="space-y-5">
                  {tiersBlock}
                  {customBlock}
                  {topupBlock}
                  {channelBlock}
                  {previewBlock}
                </div>
              </div>
            ) : (
              // From scratch: single column - pick a slot, then set deposit.
              <div className="space-y-5">
                {pieceNameInput}
                {fromScratchPicker}
                {sessionWindowBlock}
                {tiersBlock}
                {customBlock}
                {topupBlock}
                {channelBlock}
                {hasSlot ? previewBlock : null}
              </div>
            )}

            {noDepositBlock ? <div className="mt-5">{noDepositBlock}</div> : null}
            {paymentContactBlock ? <div className="mt-5">{paymentContactBlock}</div> : null}
            {sendFooter}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
